import React, { useState, useRef } from 'react';
import { PulseLoader } from 'react-spinners';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { AutoComplete } from 'primereact/autocomplete';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { InputMask } from 'primereact/inputmask';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';

import LoadingScreen from 'src/components/LoadingScreen';
import { dateToString } from 'src/utils/dateToString';
import { ModelOfPeople } from 'src/database';

import { useListAgencyPeoplesController } from 'src/controllers/People/ListAgencyPeopleController';
import { useFormCreateUnityPeoples } from 'src/controllers/People/CreateUnityPeopleController';
import { useListUnitysController } from 'src/controllers/Unitys/ListUnitysController';
import useIntersectionObserver from 'src/components/IntersectionObserver/useIntersectionObserver';

interface filteredProps {
	label: string;
	value?: string;
}

const AgencyPeoplesList: React.FC = () => {
	const { getAgencyPeopleListController, handleAgencyPeopleListController } = useListAgencyPeoplesController();
	const { getUnityPeopleFormController } = useFormCreateUnityPeoples();
	const { getUnityListController } = useListUnitysController();

	const [globalFilter, setGlobalFilter] = useState<string | null>(null);
	const [valueUnity, setValueUnity] = useState('');
	const [filteredUnity, setFilteredUnity] = useState<filteredProps[]>([{ label: '', value: '' }]);
	const [valueRole, setValueRole] = useState('');
	const [filteredRole, setFilteredRole] = useState<filteredProps[]>([{ label: '', value: '' }]);

	if (getAgencyPeopleListController.loading) {
		return <LoadingScreen />;
	}

	const unitys = getUnityListController.AllUnitysData.map(unity => {
		return { label: unity.NOME, value: unity.ID };
	});

	const roles = getUnityPeopleFormController.roles.map(role => {
		return { label: role.DESCRICAO, value: role.ID };
	});

	const searchUnitys = (event: any) => {
		let filteredUnitys;
		if (!event.query.trim().length) {
			filteredUnitys = [...unitys];
		} else {
			filteredUnitys = unitys.filter(unity => {
				return unity.label.toLowerCase().includes(event.query.toLowerCase());
			});
		}
		setFilteredUnity(filteredUnitys);
	};

	const searchRoles = (event: any) => {
		let filteredRoles;
		if (!event.query.trim().length) {
			filteredRoles = [...roles];
		} else {
			filteredRoles = roles.filter(role => {
				return role.label.toLowerCase().includes(event.query.toLowerCase());
			});
		}
		setFilteredRole(filteredRoles);
	};

	const tableHeader = (
		<div className='table-header p-grid p-d-flex p-jc-between'>
			<h5 className='p-m-0'>Pessoas</h5>
			<span className='p-input-icon-left p-search-bar'>
				<i className='pi pi-search' />
				<InputText
					type='search'
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGlobalFilter(e.target.value)}
					placeholder='Buscar...'
				/>
			</span>
		</div>
	);

	const actionBodyTemplate = (AgencyPeople: ModelOfPeople) => {
		return (
			<React.Fragment>
				<Button
					icon='pi pi-pencil'
					className='p-button-rounded p-button-text p-button-success'
					onClick={() => handleAgencyPeopleListController.openEditDialog(AgencyPeople)}
				/>
				<Button
					icon='pi pi-trash'
					className='p-button-rounded p-button-text p-button-danger'
					onClick={() => handleAgencyPeopleListController.openDeleteDialog(AgencyPeople)}
				/>
			</React.Fragment>
		);
	};

	const editAgencyPeopleDialogFooter = (
		<React.Fragment>
			<Button
				label='Cancelar'
				icon='pi pi-times'
				className='p-button-text'
				onClick={handleAgencyPeopleListController.closeEditDialog}
			/>
			<Button
				type='submit'
				label='Salvar'
				icon='pi pi-check'
				className='p-button-text'
				onClick={() => {
					getAgencyPeopleListController.formik.handleSubmit();
				}}
			/>
		</React.Fragment>
	);

	const deleteAgencyPeopleDialogFooter = (
		<React.Fragment>
			<Button
				label='Não'
				icon='pi pi-times'
				className='p-button-text'
				onClick={handleAgencyPeopleListController.closeDeleteDialog}
			/>
			<Button
				label='Sim'
				icon='pi pi-check'
				className='p-button-text'
				onClick={() => {
					getAgencyPeopleListController.selectedAgencyPeople &&
						handleAgencyPeopleListController.removeAgencyPeople(
							getAgencyPeopleListController.selectedAgencyPeople,
						);
				}}
			/>
		</React.Fragment>
	);

	const leftAgencyPeopleToolbar = () => {
		return (
			<Link to='/peopleForm'>
				<Button label='Cadastrar pessoa' icon='pi pi-plus' className='p-button-success p-mr-2' />
			</Link>
		);
	};

	const HEADER_NOME = (rowData: ModelOfPeople) => {
		return (
			<React.Fragment>
				<span className='left-header'>NOME</span>
				{rowData.NOME}
			</React.Fragment>
		);
	};

	const HEADER_CARGO = (rowData: ModelOfPeople) => {
		return (
			<React.Fragment>
				<span className='left-header'>CARGO</span>
				{rowData.DESCRICAO_CARGO}
			</React.Fragment>
		);
	};

	const HEADER_DATANASCIMENTO = (rowData: ModelOfPeople) => {
		return (
			<React.Fragment>
				<span className='left-header'>DATA DE NASCIMENTO</span>
				{dateToString(rowData.DATANASCIMENTO, 'br')}
			</React.Fragment>
		);
	};

	const HEADER_CPF = (rowData: ModelOfPeople) => {
		return (
			<React.Fragment>
				<span className='left-header'>CPF</span>
				{rowData.CPF}
			</React.Fragment>
		);
	};

	const HEADER_ORGAOUNIDADE = (rowData: ModelOfPeople) => {
		return (
			<React.Fragment>
				<span className='left-header'>UNIDADE</span>
				{rowData.NOME_ORGAOUNIDADE}
			</React.Fragment>
		);
	};

	const loadingText = () => {
		return <span className='loading-text'></span>;
	};

	const Loader: React.FC = ({ children }) => {
		const divRef: any = useRef<HTMLDivElement | null>(null);

		const [isVisible] = useIntersectionObserver({
			elementRef: divRef,
		});

		if (isVisible && !getAgencyPeopleListController.maxData && !getAgencyPeopleListController.loadingRows) {
			handleAgencyPeopleListController.setRows(getAgencyPeopleListController.rows + 20);
		}

		return (
			<div
				ref={divRef}
				style={{
					minHeight: '5px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}>
				{children}
			</div>
		);
	};

	return (
		<div>
			<Toast ref={getAgencyPeopleListController.toast} />
			<div className='p-card p-p-4 datatable-container'>
				<Toolbar className='p-mb-4' left={leftAgencyPeopleToolbar}></Toolbar>

				<DataTable
					header={tableHeader}
					globalFilter={globalFilter}
					rowHover
					className='responsive-table p-datatable-sm'
					dataKey='ID'
					scrollable
					scrollHeight={'200%'}
					value={getAgencyPeopleListController.AgencyPeoplesData}>
					<Column
						headerStyle={{ width: '300px' }}
						field='NOME'
						header='NOME'
						body={HEADER_NOME}
						loadingBody={loadingText}></Column>
					<Column
						field='DESCRICAO_CARGO'
						header='CARGO'
						body={HEADER_CARGO}
						loadingBody={loadingText}></Column>
					<Column
						field='DATANASCIMENTO'
						header='NASCIMENTO'
						body={HEADER_DATANASCIMENTO}
						loadingBody={loadingText}></Column>
					<Column field='CPF' header='CPF' body={HEADER_CPF} loadingBody={loadingText}></Column>
					<Column
						field='NOME_ORGAOUNIDADE'
						header='UNIDADE'
						body={HEADER_ORGAOUNIDADE}
						loadingBody={loadingText}></Column>
					<Column
						body={actionBodyTemplate}
						bodyStyle={{ textAlign: 'right' }}
						loadingBody={loadingText}></Column>
				</DataTable>
				{/* <Loader>{!getAgencyPeopleListController.maxData ?
                    getAgencyPeopleListController.loadingRows ? <PulseLoader color="#0388e5" size={8} /> : <></>
                    : "Lista completa"}
                </Loader> */}
			</div>

			<Dialog
				className='p-fluid dropdown-visible'
				style={{ width: '90%' }}
				visible={getAgencyPeopleListController.editDialog}
				onHide={handleAgencyPeopleListController.closeEditDialog}
				onShow={() => {
					getAgencyPeopleListController.formik.setValues({
						...getAgencyPeopleListController.selectedAgencyPeople,
						DATANASCIMENTO: dateToString(
							getAgencyPeopleListController.selectedAgencyPeople.DATANASCIMENTO,
							'br',
						),
					});
					setValueRole(getAgencyPeopleListController.selectedAgencyPeople.DESCRICAO_CARGO || '');
					setValueUnity(getAgencyPeopleListController.selectedAgencyPeople.NOME_ORGAOUNIDADE || '');
				}}
				footer={editAgencyPeopleDialogFooter}
				header={`Editar pessoa ${getAgencyPeopleListController.selectedAgencyPeople?.NOME}`}
				modal>
				{getAgencyPeopleListController.loadingEdit ? <LoadingScreen color='#0388e5' /> : <></>}
				<div className='p-grid p-field' style={{ zIndex: 9999 }}>
					<div className='p-field p-col-12 p-md-2'>
						<label htmlFor='orgao'>CPF</label>
						<InputMask
							id='cpf'
							disabled
							mask='999.999.999-99'
							{...getAgencyPeopleListController.formik.getFieldProps('CPF')}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getAgencyPeopleListController.formik.touched.CPF &&
									getAgencyPeopleListController.formik.errors.CPF,
								'popup-form-inactive': !(
									getAgencyPeopleListController.formik.touched.CPF &&
									getAgencyPeopleListController.formik.errors.CPF
								),
							})}>
							{getAgencyPeopleListController.formik.errors.CPF}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-8'>
						<label htmlFor='nome'>Nome</label>
						<InputText
							id='nome'
							{...getAgencyPeopleListController.formik.getFieldProps('NOME')}
							onChange={getAgencyPeopleListController.formik.handleChange('NOME')}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getAgencyPeopleListController.formik.touched.NOME &&
									getAgencyPeopleListController.formik.errors.NOME,
								'popup-form-inactive': !(
									getAgencyPeopleListController.formik.touched.NOME &&
									getAgencyPeopleListController.formik.errors.NOME
								),
							})}>
							{getAgencyPeopleListController.formik.errors.NOME}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-2'>
						<label htmlFor='email'>Data de nascimento</label>
						<InputMask
							mask='99/99/9999'
							placeholder='__/__/____'
							{...getAgencyPeopleListController.formik.getFieldProps('DATANASCIMENTO')}
							onChange={e =>
								getAgencyPeopleListController.formik.setFieldValue('DATANASCIMENTO', e.value)
							}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getAgencyPeopleListController.formik.touched.DATANASCIMENTO &&
									getAgencyPeopleListController.formik.errors.DATANASCIMENTO,
								'popup-form-inactive': !(
									getAgencyPeopleListController.formik.touched.DATANASCIMENTO &&
									getAgencyPeopleListController.formik.errors.DATANASCIMENTO
								),
							})}>
							{getAgencyPeopleListController.formik.errors.DATANASCIMENTO}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-4'>
						<label htmlFor='telefone'>Cargo</label>
						<AutoComplete
							id='cargo'
							value={valueRole}
							field='label'
							placeholder='Buscar...'
							onChange={e => {
								setValueRole(e.value);
								getAgencyPeopleListController.formik.setFieldValue(
									'CARGO',
									filteredRole.find(role => {
										return e.value === role.label;
									})?.value || '',
								);
							}}
							onSelect={e => {
								setValueRole(e.value.label);
								getAgencyPeopleListController.formik.setFieldValue('CARGO', e.value.value);
							}}
							suggestions={filteredRole}
							completeMethod={searchRoles}
							dropdown
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getAgencyPeopleListController.formik.touched.CARGO &&
									getAgencyPeopleListController.formik.errors.CARGO,
								'popup-form-inactive': !(
									getAgencyPeopleListController.formik.touched.CARGO &&
									getAgencyPeopleListController.formik.errors.CARGO
								),
							})}>
							{getAgencyPeopleListController.formik.errors.CARGO}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-8'>
						<label htmlFor='telefone'>Unidade</label>
						<AutoComplete
							id='unidade'
							value={valueUnity}
							placeholder='Buscar...'
							field='label'
							panelStyle={{ zIndex: 0 }}
							onChange={e => {
								setValueUnity(e.value);
								getAgencyPeopleListController.formik.setFieldValue(
									'ORGAOUNIDADE',
									filteredUnity.find(unity => {
										return e.value === unity.label;
									})?.value || '',
								);
							}}
							onSelect={e => {
								setValueUnity(e.value.label);
								getAgencyPeopleListController.formik.setFieldValue('ORGAOUNIDADE', e.value.value);
							}}
							suggestions={filteredUnity}
							completeMethod={searchUnitys}
							dropdown
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getAgencyPeopleListController.formik.touched.ORGAOUNIDADE &&
									getAgencyPeopleListController.formik.errors.ORGAOUNIDADE,
								'popup-form-inactive': !(
									getAgencyPeopleListController.formik.touched.ORGAOUNIDADE &&
									getAgencyPeopleListController.formik.errors.ORGAOUNIDADE
								),
							})}>
							{getAgencyPeopleListController.formik.errors.ORGAOUNIDADE}
						</div>
					</div>
				</div>
			</Dialog>

			<Dialog
				style={{ width: '450px' }}
				visible={getAgencyPeopleListController.deleteDialog}
				onHide={handleAgencyPeopleListController.closeDeleteDialog}
				footer={deleteAgencyPeopleDialogFooter}
				header='Remover pessoa'
				modal>
				<div className='confirmation-content'>
					<i className='pi pi-exclamation-triangle p-mr-3' style={{ fontSize: '2rem' }} />
					{getAgencyPeopleListController.selectedAgencyPeople && (
						<span>
							Você tem certeza que deseja deletar{' '}
							<b>{getAgencyPeopleListController.selectedAgencyPeople.NOME}</b>?
						</span>
					)}
				</div>
			</Dialog>
		</div>
	);
};

export default AgencyPeoplesList;
